import { Component, ElementRef, HostListener, Inject, inject, NgZone, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from "rxjs";
import { AddressModel } from "../../models/address.model";
import PlacesService = google.maps.places.PlacesService;
import { GooglePlacesService } from "../../services/google-places.service";
import { AfterViewInit } from '@angular/core';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, AfterViewInit {
  private _googlePlacesService: GooglePlacesService = inject(GooglePlacesService);
  private _ngZone: NgZone = inject(NgZone);
  private _el: ElementRef = inject(ElementRef);
  private _renderer: Renderer2 = inject(Renderer2);

  pickUpAddress!: string;
  addressSearch$: Subject<string> = new Subject<string>();
  selectedAddress!: any;
  loadingAddress!: boolean;
  addressSuggestions: AddressModel[] = [];
  private _platformId: Object = inject(PLATFORM_ID);
  blurReady: boolean = false;

  constructor() {
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this.getPickUpAddress();
    }

    this.addressSearch$
      .pipe(
        tap(() => this.loadingAddress = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this._googlePlacesService.getPlacePredictions(query))
      )
      .subscribe({
        next: (results: google.maps.places.AutocompletePrediction[]) => {
          this.loadingAddress = false;
          this.addressSuggestions = results.map((prediction): any => ({
            placeId: prediction.place_id,
            displayName: prediction.description,
            mainText: prediction.structured_formatting.main_text,
            secondaryText: prediction.structured_formatting.secondary_text
          }));
        },
        error: () => this.loadingAddress = false
      });
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.blurReady = true;
        }, 1200);
      });
    });
  }

  getPickUpAddress(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const pickupAddress: string | null = localStorage.getItem('pickup-address');
    const selectedAddress: string | null = localStorage.getItem('selected-address');

    if (pickupAddress && selectedAddress) {
      this.pickUpAddress = pickupAddress;
      this.selectedAddress = JSON.parse(selectedAddress);
    }
  }

  goToForm(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const el = document.querySelector('#pick-up-request');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  onAddressSearch(event: Event): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const query: string = input?.value.trim() || '';

    if (query.length > 1) {
      this.addressSearch$.next(query);
    } else {
      this.addressSuggestions = [];
      this.loadingAddress = false;
    }
  }

  async selectAddress(address: AddressModel): Promise<void> {
    try {
      const placesService: PlacesService = new google.maps.places.PlacesService(document.createElement('div'));

      placesService.getDetails({ placeId: address.placeId }, (place: any, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          this._ngZone.run(() => {
            this.selectedAddress = {
              display_name: address.displayName,
              address: {
                postcode: place.address_components?.find((c: any) => c.types.includes("postal_code"))?.long_name || '',
                city: place.address_components?.find((c: any) => c.types.includes("locality"))?.long_name ||
                  place.address_components?.find((c: any) => c.types.includes("administrative_area_level_2"))?.long_name || '',
                state: place.address_components?.find((c: any) => c.types.includes("administrative_area_level_1"))?.long_name || '',
                country_code: place.address_components?.find((c: any) => c.types.includes("country"))?.short_name || '',
              },
              lon: place.geometry?.location?.lng().toString() || '',
              lat: place.geometry?.location?.lat().toString() || ''
            };

            this.pickUpAddress = address.displayName;
            if (isPlatformBrowser(this._platformId)) {
              localStorage.setItem('pickup-address', this.pickUpAddress);
              localStorage.setItem('selected-address', JSON.stringify(this.selectedAddress));
            } else {
              console.error('Skipping localStorage access on server');
            }
            this.addressSuggestions = [];
          });
        }
      });

    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  }

  @HostListener('window:scroll', [])
  onScroll() {
    if (!isPlatformBrowser(this._platformId)) return;
    const scrollY = window.scrollY || window.pageYOffset;
    const newSize = 45 + Math.min(scrollY / 10, 35);
    this._renderer.setStyle(this._el.nativeElement, '--rocket-bg-size', `${newSize}%`);
  }
}
